<template>
  <div>
    <div class="px-4 py-2 flex items-center justify-between mb-4">
      <h3
        id="pages.title"
        class="text-lg text-center font-medium text-gray-900 flex-grow"
      >
        {{ translate("Pages") }}
      </h3>
      <button
        id="pages.opencreatepagemodal"
        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        @click="isNewPageModalOpen = true"
      >
        <i class="ni ni-fat-add mr-2"></i>
        <span>{{ translate("New Page") }}</span>
      </button>
    </div>

    <div class="container mx-auto px-4">
      <!-- Page Actions -->

      <!-- Search Filter -->
      <div class="mb-4 bg-gray-100 rounded-lg p-4">
        <form @submit.prevent="searchPage">
          <div class="flex flex-col md:flex-row md:items-center">
            <div class="md:w-7/12 md:mb-0 sm:mb-2">
              <input
                type="text"
                id="pages.searchlabel"
                :placeholder="translate('Enter page name')"
                class="w-full px-3 py-2 placeholder-gray-400 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                v-model="pageName"
              />
            </div>
            <div class="md:w-5/12 md:pl-4 flex">
              <button
                id="pages.filtersearch"
                class="mr-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {{ translate("SEARCH") }}
              </button>
              <button
                id="pages.clearsearch"
                type="button"
                @click="resetForm"
                class="inline-flex mr-3 items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {{ translate("CLEAR") }}
              </button>
            </div>
          </div>
        </form>
      </div>

      <!-- Page List Component -->
      <page-list
        :list="list"
        :loading="loading"
        @openEditPageModal="openEditPageModal"
        @deletePage="confirmDeletePage"
        @getPages="getPages"
        :listMeta="listMeta"
      ></page-list>
    </div>

    <!-- Modals -->

    <PageModal
      v-if="isNewPageModalOpen"
      :isOpen="isNewPageModalOpen"
      @close="isNewPageModalOpen = false"
      @getPages="getPages"
    />

    <PageModal
      v-if="isEditPageModalOpen"
      :isOpen="isEditPageModalOpen"
      :existingPage="existingPageData"
      @close="isEditPageModalOpen = false"
      @updatePage="updatePage"
    />

    <!-- Delete Confirmation Modal -->
    <BaseModal
      v-if="showDeleteConfirm"
      :title="translate('Delete Page')"
      @close="cancelDeletePage"
      headerClass="py-3"
    >
      <template v-slot:body>
        <div class="flex items-center py-3">
          <div
            class="mr-2 flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100 sm:mx-0"
          >
            <i class="fas fa-trash-alt text-red-600"></i>
          </div>
          <div class="text-center sm:text-left">
            <p class="text-lg mb-0 font-medium">
              {{ translate("Are you sure you want to delete this page?") }}
            </p>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <div class="w-full flex justify-end py-3">
          <button
            id="pages.closedeletepagemodal"
            @click="cancelDeletePage"
            class="rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            {{ translate("Cancel") }}
          </button>
          <button
            id="pages.deletepage"
            @click="executeDeletePage"
            class="rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            {{ translate("Delete") }}
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { eventHandler, translations } from '@/mixins';
import PageList from './components/PageList';
import PageModal from './components/PageModal.vue';
import { Page as PageApi } from '@/services/SOLO';
import { BaseModal } from '@/components';

export default {
  name: 'Pages',

  components: {
    PageList,
    PageModal,
    BaseModal,
  },

  mixins: [eventHandler, translations],

  computed: {
    ...mapGetters({
      getLocale: 'app/getLocale',
    }),
    existingPageData () {
      const page = this.list.find((item) => item.id === this.editId);
      if (!page) return null;
      return JSON.parse(JSON.stringify({ id: page.id, ...page.attributes }));
    },
  },

  data () {
    return {
      isNewPageModalOpen: false,
      isEditPageModalOpen: false,
      list: [],
      listMeta: null,
      loading: false,
      pageName: '',
      editId: null,
      page: 1,
      size: 10,
      showDeleteConfirm: false,
      pageToDelete: null,
    };
  },

  mounted () {
    this.getPages(this.page, this.size);
  },

  methods: {
    async getPages (page, size) {
      this.loading = true;
      try {
        const response = await PageApi.all(page, size);
        this.list = response.data.data;
        this.listMeta = response.data.meta.pagination;
        this.loading = false;
        this.pageName = '';
      } catch (error) {
        this.loading = false;
        console.error('Error fetching pages:', error);
      }
    },

    openEditPageModal (open, id) {
      this.isEditPageModalOpen = open;
      this.editId = id;
    },

    confirmDeletePage (pageId) {
      this.pageToDelete = pageId;
      this.showDeleteConfirm = true;
    },

    cancelDeletePage () {
      this.showDeleteConfirm = false;
      this.pageToDelete = null;
    },

    executeDeletePage () {
      if (this.pageToDelete) {
        PageApi.deletePage(this.pageToDelete)
          .then((response) => {
            this.getPages(this.page, this.size);
            this.$notify({
              title: 'PAGE DELETED!',
              verticalAlign: 'bottom',
              horizontalAlign: 'left',
              message: 'Page has been deleted successfully',
              type: 'success',
              icon: 'fas fa-check',
            });
          })
          .catch((err) => {
            this.$notify({
              title: 'SYSTEM ERROR!',
              verticalAlign: 'bottom',
              horizontalAlign: 'left',
              message: 'Something went wrong, please try again!',
              type: 'danger',
              icon: 'fas fa-bomb',
            });
          })
          .finally(() => {
            this.showDeleteConfirm = false;
            this.pageToDelete = null;
          });
      }
    },

    updatePage (data) {
      const index = this.list.findIndex((item) => item.id == this.editId);
      if (index !== -1) {
        this.list.splice(index, 1, data);
      }
    },

    async searchPage () {
      this.loading = true;
      try {
        const response = await PageApi.search(this.pageName);
        this.list = response.data.data;
        this.listMeta = response.data.meta.pagination;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error('Error searching pages:', error);
      }
    },

    resetForm () {
      this.pageName = '';
      this.getPages(this.page, this.size);
    },
  },
};
</script>
